import { Injectable } from '@angular/core';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  CreatePaymentMilestoneInput,
  GqlService,
  UpdatePaymentMilestoneInput,
} from '@services/gql.service';
import { MainQuery } from 'src/app/layouts/main-layout/state/main.query';
import { OverlayService } from '@services/overlay.service';
import { PaymentMilestoneStore } from './payment-milestone.store';
import { batchPromises } from '@utils/batch-promises';

@Injectable({ providedIn: 'root' })
export class PaymentMilestoneService {
  constructor(
    private paymentMilestoneStore: PaymentMilestoneStore,
    private gqlService: GqlService,
    private mainQuery: MainQuery,
    private overlayService: OverlayService
  ) {}

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.paymentMilestoneStore.setLoading(true);
        this.paymentMilestoneStore.remove(() => true);

        return this.gqlService.listPaymentMilestones$().pipe(
          map(({ errors, success, data }) => {
            if (success && data) {
              this.paymentMilestoneStore.set(data);
            } else {
              this.overlayService.error(errors);
            }

            this.paymentMilestoneStore.setLoading(false);
          })
        );
      })
    );
  }

  async upsert(upsertData: (CreatePaymentMilestoneInput & { id: string | null })[]) {
    const proms: Promise<{ success: boolean; errors: string[] }>[] = [];
    const allErrors: string[][] = [];

    upsertData.forEach((mil) => {
      const { id, amount, name, organization_id, target_date } = mil;
      if (id) {
        const prom = this.gqlService
          .updatePaymentMilestone$({ id, amount, name, organization_id, target_date })
          .pipe(
            tap(({ success, data, errors }) => {
              if (success && data) {
                this.paymentMilestoneStore.update(data.id, data);
              } else {
                allErrors.push(errors);
              }
            })
          )
          .toPromise();
        proms.push(prom);
      } else {
        const prom = this.gqlService
          .createPaymentMilestone$({ amount, name, organization_id, target_date })
          .pipe(
            tap(({ success, data, errors }) => {
              if (success && data) {
                this.paymentMilestoneStore.add(data);
              } else {
                allErrors.push(errors);
              }
            })
          )
          .toPromise();
        proms.push(prom);
      }
    });

    await batchPromises(proms, (p) => p);

    if (allErrors.length) {
      this.overlayService.error(...allErrors);
    }

    return !!allErrors.length;
  }

  async add(paymentMilestone: CreatePaymentMilestoneInput) {
    const { errors, success, data } = await this.gqlService
      .createPaymentMilestone$(paymentMilestone)
      .toPromise();

    if (success && data) {
      this.paymentMilestoneStore.add(data);
    } else {
      this.overlayService.error(errors);
    }

    return {
      success,
      errors,
      data,
    };
  }

  async update(paymentMilestone: UpdatePaymentMilestoneInput) {
    const { success, errors, data } = await this.gqlService
      .updatePaymentMilestone$(paymentMilestone)
      .toPromise();

    if (success && data) {
      this.paymentMilestoneStore.update(data.id, data);
    } else {
      this.overlayService.error(errors);
    }

    return {
      success,
      errors,
      data,
    };
  }

  async remove(ids: string[]) {
    const proms: Promise<any>[] = [];
    const allErrors: string[][] = [];
    ids.forEach((id) => {
      proms.push(
        this.gqlService
          .removePaymentMilestone$(id)
          .pipe(
            tap(({ success, errors, data }) => {
              if (success && data) {
                this.paymentMilestoneStore.remove(id);
              } else {
                allErrors.push(errors);
              }
            })
          )
          .toPromise()
      );
    });

    await batchPromises(proms, (p) => p);

    if (allErrors.length) {
      this.overlayService.error(...allErrors);
    }

    return !!allErrors.length;
  }
}
